import datacentral from '@/utils/axios/datacentral'
import automation from '@/utils/axios/automation'
import {
    DataGridPayload,
    ResponseArray,
} from 'vnetwork-ui/dist/components/GokuDataGrid'
import qs from 'query-string'
import {
    Cv,
    CVAnalysisResult,
    CVAnalyzeRequest,
    CvSource,
    InterviewMailValues,
} from '../types'
import { Attachment } from '@/modules/Upload/types'
import { ErrorResponse, ResponseObject } from '@/types/shared'
import { CreateCVSourceFormValues } from '@/modals/ResumeModals/CreateCVSource'
export const getCVs = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<Cv>>(
        `/cv?${qs.stringify(payload)}`
    )
    return response?.data
}
export const getCvSources = async (): Promise<ResponseArray<CvSource>> => {
    const response = await datacentral.get('cv-sources')
    return response.data
}
export const getCvSourcesOptions = async () => {
    const response = await getCvSources()
    return response.results.map((item) => ({
        label: item?.cover_letter_source_name,
        value: item?.cover_letter_source_id,
    }))
}

export const getCvSourcesSelect = async () => {
    const response = await getCvSources()
    return response.results.map((item) => ({
        label: item?.cover_letter_source_name,
        value: item?.cover_letter_source_name,
    }))
}
export const createCV = async ({
    payload,
}: {
    payload: {
        cv: Partial<Cv>
        attachment?: Partial<Attachment>
    }
}): Promise<ResponseObject<Cv> | ErrorResponse> => {
    const response = await datacentral.post<ResponseObject<Cv>>('/cv', payload)
    return response.data
}
export const updateCV = async (payload: {
    id: number
    cv: Cv
}): Promise<ResponseObject<Cv> | ErrorResponse> => {
    const response = await datacentral.put(`/cv/${payload.id}`, payload.cv)
    return response.data
}
export const getCVByID = async (id: number) => {
    const response = await datacentral.get<ResponseObject<Cv>>(`/cv/${id}`)
    return response?.data
}

export const exportCVs = async (payload: DataGridPayload) => {
    try {
        const queryString = qs.stringify(payload)

        const response = await datacentral.get(`/cv/export?${queryString}`, {
            responseType: 'blob',
        })

        return response.data
    } catch (error) {
        console.error('Lỗi khi tải file Excel:', error)
        throw error
    }
}
export const deleteCV = async (
    id: number
): Promise<ResponseObject<Cv> | ErrorResponse> => {
    const response = await datacentral.delete(`/cv/${id}`)
    return response.data
}
export const createCVSource = async (
    values: CreateCVSourceFormValues
): Promise<ResponseObject<CvSource>> => {
    const response = await datacentral.post('/cv-sources', values)
    return response.data
}
export const sendMail = async ({
    id,
    mailTemplate,
    mail_send = [],
}: {
    id: number
    mailTemplate: string
    mail_send?: string[]
}): Promise<ResponseObject<Cv>> => {
    const response = await datacentral.post<ResponseObject<Cv>>(
        `/cv/${id}/send_mail`,
        {
            id,
            mailTemplate,
            mail_send,
        }
    )
    return response.data
}
export const sendMailInterview = async ({
    id,
    values,
}: {
    id: number
    values: InterviewMailValues
}): Promise<ResponseObject<Cv>> => {
    const response = await datacentral.post<ResponseObject<Cv>>(
        `/cv/${id}/send_mail_interview`,
        values
    )
    return response.data
}

export const getCVPreviewEmail = async ({
    cvID,
    payload,
}: {
    cvID: number
    payload: InterviewMailValues
}): Promise<string> => {
    const response = await datacentral.post(`/cv/${cvID}/preview`, payload)
    return response?.data
}

export interface HRTemplate {
    mailer_template_id: number
    mailer_template_subject: string
    mailer_template_content: string
    mailer_template_type: string
    mailer_template_created_at: string
    mailer_template_updated_at: string
}

export const getHRTemplates = async () => {
    const response = await automation.get<ResponseArray<HRTemplate>>(
        '/mailers/datacentral/templates/hr'
    )
    return response?.data?.results ?? []
}

export interface CVMailerTracking {
    mailer_id: number
    mailer_subject: string
    mailer_referrer_id: string
    mailer_referrer_type: string
    mailer_template_id?: string | number | null
    mailer_receiver: string[]
    mailer_cc: string[]
    mailer_bcc: string[]
    mailer_status: 'sent' | 'failed' | 'pending'
    mailer_send_at?: string | null
    mailer_performed_at?: string | null
    metadata?: {
        template?: string
        position_name?: string
        name_candidate?: string
        email_candidate?: string
    }
}

/**
 * Analyze a freshly-uploaded CV file. Stateless — server does not persist.
 * Used by the Create flow to pre-fill the candidate form and surface rich CV data.
 * When job_id or jd is provided, the response includes an `evaluation` block.
 */
export const analyzeCV = async (
    file: File,
    payload?: CVAnalyzeRequest
): Promise<ResponseObject<CVAnalysisResult>> => {
    const formData = new FormData()
    formData.append('file', file)
    if (payload?.job_id !== undefined)
        formData.append('job_id', String(payload.job_id))
    if (payload?.jd) formData.append('jd', payload.jd)
    const response = await datacentral.post<ResponseObject<CVAnalysisResult>>(
        '/cv/analyze',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
}

/**
 * Re-analyze an existing CV record. Server downloads the stored file,
 * runs analysis, and persists the result to cover_letters.cv_analysis.
 */
export const analyzeCVByID = async (
    cvID: number,
    payload?: CVAnalyzeRequest
): Promise<ResponseObject<CVAnalysisResult>> => {
    const response = await datacentral.post<ResponseObject<CVAnalysisResult>>(
        `/cv/${cvID}/analyze`,
        payload ?? {}
    )
    return response.data
}

export const getCVMailerTrackings = async (
    payload: DataGridPayload & {
        cv_id?: number
        mailer_id?: number
        email?: string
    }
) => {
    const queryParams = new URLSearchParams()
    if (payload.cv_id) queryParams.append('cv_id', String(payload.cv_id))
    if (payload.page) queryParams.append('page', String(payload.page))
    if (payload.page_size)
        queryParams.append('page_size', String(payload.page_size))
    if (payload.mailer_id)
        queryParams.append('mailer_id', String(payload.mailer_id))
    if (payload.email) queryParams.append('email', payload.email)

    const response = await automation.get<ResponseArray<CVMailerTracking>>(
        `/mailers/datacentral/cv/trackings?${queryParams.toString()}`
    )

    // Transform to ResponseArray format for GokuDataGrid
    return response?.data
}
